import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/cn";
import type { StreamEvent } from "@career-ops-amplify/shared";

interface RunDetail {
  id: string;
  kind: string;
  input: string;
  status: "pending" | "running" | "done" | "error";
  progress?: string;
  result?: unknown;
  error?: string;
  tokens?: number;
  costUsd?: number | null;
  createdAt: string;
  updatedAt?: string;
}

export function JobPage() {
  const { id } = useParams<{ id: string }>();
  const [events, setEvents] = useState<StreamEvent[]>([]);
  const [streamActive, setStreamActive] = useState(false);
  const streamRef = useRef<AbortController | null>(null);
  const outputRef = useRef<HTMLDivElement>(null);

  // Fetch run detail
  const { data: run, isLoading, error } = useQuery<RunDetail>({
    queryKey: ["run", id],
    queryFn: async () => {
      const resp = await fetch(`/api/run/${id}`);
      if (!resp.ok) throw new Error("Run not found");
      return resp.json();
    },
    enabled: !!id,
    refetchInterval: (query) => {
      const state = query.state.data;
      if (state && (state.status === "running" || state.status === "pending")) {
        return 2000; // Poll every 2s while active
      }
      return false;
    },
  });

  // Auto-scroll output
  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [events]);

  // Stream events for running jobs
  useEffect(() => {
    if (!run || run.status !== "running") return;

    const abortController = new AbortController();
    streamRef.current = abortController;
    setStreamActive(true);

    async function streamEvents() {
      try {
        const resp = await fetch(`/api/run/${id}/stream`, {
          signal: abortController.signal,
        });
        if (!resp.ok) return;

        const reader = resp.body?.getReader();
        if (!reader) return;

        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() ?? "";

          for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed) continue;
            try {
              const parsed = JSON.parse(trimmed) as StreamEvent;
              setEvents((prev) => [...prev, parsed]);
            } catch {
              // Skip malformed lines
            }
          }
        }
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
      } finally {
        setStreamActive(false);
      }
    }

    streamEvents();

    return () => {
      abortController.abort();
      setStreamActive(false);
    };
  }, [run?.status, id]);

  const statusColor = {
    pending: "bg-amber-500/10 text-amber-400",
    running: "bg-sky-500/10 text-sky-400",
    done: "bg-emerald-500/10 text-emerald-400",
    error: "bg-red-500/10 text-red-400",
  };

  if (isLoading) {
    return (
      <>
        <div className="flex items-center justify-center py-16">
          <div className="flex flex-col items-center gap-3">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
            <p className="text-sm text-muted-foreground">Loading job details…</p>
          </div>
        </div>
      </>
    );
  }

  if (error || !run) {
    return (
      <>
        <Link
          to="/explore"
          className="mb-4 inline-flex items-center gap-1 text-sm text-brand hover:underline"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
          </svg>
          Back to explore
        </Link>
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3">
          <p className="text-sm text-destructive">
            Run not found or failed to load.
          </p>
        </div>
      </>
    );
  }

  const textOutput = events
    .filter((e): e is StreamEvent & { type: "text" } => e.type === "text")
    .map((e) => e.text)
    .join("");

  const statusEvents = events.filter(
    (e): e is StreamEvent & { type: "status" } => e.type === "status",
  );

  return (
    <>
      {/* Back link */}
      <Link
        to="/explore"
        className="mb-6 inline-flex items-center gap-1 text-sm text-brand hover:underline"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
        </svg>
        Back to explore
      </Link>

      {/* Header card */}
      <div className="mb-6 rounded-lg border border-border bg-card p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="mb-2 flex items-center gap-2">
              <span className="text-xs font-mono text-muted-foreground">
                #{run.id.slice(0, 8)}
              </span>
              <span className="text-xs text-muted-foreground">|</span>
              <span
                className={cn(
                  "rounded-full px-2 py-0.5 text-xs font-medium",
                  statusColor[run.status],
                )}
              >
                {run.status}
              </span>
            </div>
            <h1 className="text-xl font-bold text-foreground">
              {run.kind.charAt(0).toUpperCase() + run.kind.slice(1)} Run
            </h1>
          </div>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            {run.tokens !== undefined && (
              <span>
                Tokens: <strong className="text-foreground">{run.tokens.toLocaleString()}</strong>
              </span>
            )}
            {run.costUsd !== null && run.costUsd !== undefined && (
              <span>
                Cost: <strong className="text-foreground">${run.costUsd.toFixed(4)}</strong>
              </span>
            )}
          </div>
        </div>

        {/* Input */}
        <div className="mt-4 border-t border-border pt-4">
          <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Input
          </p>
          <pre className="max-h-24 overflow-auto whitespace-pre-wrap break-all rounded-md bg-bg p-3 text-xs text-muted-foreground">
            {run.input}
          </pre>
        </div>

        {/* Metadata */}
        <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
          <span>Created: {new Date(run.createdAt).toLocaleString()}</span>
          {run.updatedAt && (
            <span>Updated: {new Date(run.updatedAt).toLocaleString()}</span>
          )}
        </div>
      </div>

      {/* Streaming output */}
      <div className="rounded-lg border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Output
          </span>
          <div className="flex items-center gap-2">
            {streamActive && (
              <div className="flex items-center gap-1.5">
                <div className="h-2 w-2 animate-pulse rounded-full bg-sky-400" />
                <span className="text-xs text-sky-400">Streaming</span>
              </div>
            )}
            {statusEvents.length > 0 && (
              <span className="text-xs text-muted-foreground">
                {statusEvents.at(-1)?.label}
              </span>
            )}
          </div>
        </div>

        <div
          ref={outputRef}
          className="max-h-[500px] overflow-auto p-4"
        >
          {events.length === 0 && run.status === "pending" && (
            <p className="text-sm text-muted-foreground">
              Waiting for the run to start…
            </p>
          )}

          {events.length === 0 && run.status === "done" && !run.error && (
            <p className="text-sm text-muted-foreground">
              {run.result
                ? JSON.stringify(run.result, null, 2)
                : "Run completed with no output."}
            </p>
          )}

          {events.map((event, i) => {
            switch (event.type) {
              case "text":
                return (
                  <pre
                    key={i}
                    className="whitespace-pre-wrap text-sm text-foreground"
                  >
                    {(event as StreamEvent & { text: string }).text}
                  </pre>
                );
              case "status":
                return (
                  <div
                    key={i}
                    className="my-2 flex items-center gap-2 text-xs text-muted-foreground"
                  >
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-sky-400" />
                    {(event as StreamEvent & { label: string }).label}
                  </div>
                );
              case "error":
                return (
                  <div
                    key={i}
                    className="my-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive"
                  >
                    {(event as StreamEvent & { message: string }).message}
                  </div>
                );
              case "done":
                return (
                  <div
                    key={i}
                    className="my-2 rounded-md bg-success/10 px-3 py-2 text-sm text-success"
                  >
                    Completed
                    {(event as StreamEvent & { tokens?: number }).tokens
                      ? ` — ${(event as StreamEvent & { tokens: number }).tokens.toLocaleString()} tokens used`
                      : ""}
                  </div>
                );
              default:
                return (
                  <pre
                    key={i}
                    className="text-xs text-muted-foreground"
                  >
                    {JSON.stringify(event, null, 2)}
                  </pre>
                );
            }
          })}

          {/* Error state */}
          {run.status === "error" && (
            <div className="mt-4 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3">
              <p className="text-sm font-medium text-destructive">Error</p>
              <p className="mt-1 text-sm text-destructive/80">
                {run.error ?? "An unknown error occurred."}
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}